import { isEqual, sortBy } from 'lodash';
import {
  ProjectDayTimeTracked,
  ProjectTimeEntry,
} from '../../../Projects/types';
import {
  mapToFormData,
  mapToProjectTimeEntry,
} from '../components/ProjectDayTimeEntriesForm/mapping';
import { notEmpty } from '../../../../../../utils/array';
import { ExtraFieldInternalValue } from '../../../../components/ExtraFields/extraFieldInternalValue';
import { TFunction } from 'i18next';

export const getSubmitDisabledTitle = (
  validationMessages: string[],
  isInProgress: boolean,
  t: TFunction,
): string | undefined => {
  if (validationMessages.length > 0) {
    return validationMessages.join(' • ');
  }
  if (isInProgress) {
    return t('timeTrackingNew.dayCard.saveChangesDisabledInProgress');
  }
  return undefined;
};

// 'Master' is the codebase's sentinel for "no record type" (see
// useExtraFieldsFormSimple7.tsx / useExtraFieldsFormSimple.tsx), while the
// server represents the same thing as a null recordTypeName.
const NO_RECORD_TYPE_NAME = 'Master';

// The server sends every extra field of the record type, including the ones
// left empty, while the form only re-emits the fields it actually rendered
// (collectFormDataExtraFields walks the rendered `customerField:` keys). An
// empty field carries no data either way, so dropping it on both sides is what
// makes the two shapes comparable. Real edits still differ: clearing a field
// removes it only from the current side, filling one adds it only there.
const hasExtraFieldValue = ({ internalValue }: ExtraFieldInternalValue) =>
  internalValue?.value !== null &&
  internalValue?.value !== undefined &&
  internalValue.value !== '';

// Server-shaped entries and the entries the form re-emits describe the same
// data in different shapes (untrimmed notes, synthetic RecordTypeName field,
// record type label vs developer name, no stable order), so both sides are put
// through the form round-trip before being compared. The round-trip also drops
// timesheetId/date/dateId, which are not part of the comparison.
export const normalizeProjectEntriesForComparison = (
  entries: Array<ProjectDayTimeTracked | ProjectTimeEntry>,
): ProjectTimeEntry[] =>
  sortBy(
    entries
      .map((entry) => {
        const mapped = mapToProjectTimeEntry(mapToFormData(entry));
        return (
          mapped && {
            ...mapped,
            recordTypeName: mapped.recordTypeName ?? NO_RECORD_TYPE_NAME,
            extraFields: sortBy(
              mapped.extraFields.filter(
                (field) =>
                  field.fieldApiName !== 'RecordTypeName' &&
                  hasExtraFieldValue(field),
              ),
              'fieldApiName',
            ),
          }
        );
      })
      .filter(notEmpty),
    'id',
  );

export const isDirtyProjectEntries = (
  entries1: Array<ProjectDayTimeTracked | ProjectTimeEntry>,
  entries2: Array<ProjectDayTimeTracked | ProjectTimeEntry>,
): boolean => {
  const normalized1 = normalizeProjectEntriesForComparison(entries1);
  const normalized2 = normalizeProjectEntriesForComparison(entries2);

  return !isEqual(normalized1, normalized2);
};
